import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import './FileUpload.css';

const FileUpload = () => {
  const navigate = useNavigate();
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [excelData, setExcelData] = useState([]);
  const [parsingError, setParsingError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [processingFiles, setProcessingFiles] = useState(new Set()); // Track files being processed
  const [processedFiles, setProcessedFiles] = useState(new Set()); // Track files that have been processed

  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin', { replace: true });
    }
  }, [navigate]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (fileList) => {
    const newFiles = Array.from(fileList).map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file: file,
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'pending',
      uploadTime: new Date().toLocaleTimeString()
    }));
    setFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (id) => {
    setFiles(prev => prev.filter(file => file.id !== id));
    // Clear processed files tracking
    setProcessedFiles(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
    // Clear data if no files left
    if (files.length === 1) {
      setExcelData([]);
    }
  };

  // Process Excel file after upload - SIMPLIFIED FOR NEW DYNAMIC CELL STRUCTURE
  const processExcelFile = async (fileObj) => {
    // Check if file is already being processed to prevent duplicates
    if (processingFiles.has(fileObj.id)) {
      return;
    }

    // Check if file has already been processed
    if (processedFiles.has(fileObj.id)) {
      return;
    }

    // Only process Excel/CSV files
    if (fileObj.file.type.includes('excel') || 
        fileObj.file.type.includes('spreadsheet') || 
        fileObj.file.type.includes('xlsx') || 
        fileObj.file.type.includes('xls') ||
        fileObj.file.type.includes('csv')) {
      
      // Mark file as being processed
      setProcessingFiles(prev => new Set(prev).add(fileObj.id));
      
          try {
            // Check if we already have data for this file to prevent duplicate saves
        const currentFileSignature = `${fileObj.name}_${fileObj.size}`;
            const existingFileSignature = localStorage.getItem('lastProcessedFile');
            
            if (existingFileSignature === currentFileSignature) {
          setUploadSuccess(`Excel file already processed!`);
              setParsingError(''); // Clear any previous errors
              setTimeout(() => setUploadSuccess(''), 8000); // Clear success message after 8 seconds
              return;
            }
            
            // Store current file signature to prevent immediate re-processing
            localStorage.setItem('lastProcessedFile', currentFileSignature);
            
        // Upload file directly to backend - backend will handle Excel parsing with new dynamic cell structure
        const result = await saveToDatabase(fileObj.file);
        
        // Display success message with file info
        if (result.data) {
          setUploadSuccess(`Excel file uploaded successfully! ${result.data.totalRows || 0} rows processed with dynamic cell structure.`);
          setTimeout(() => setUploadSuccess(''), 8000);
        }
            
            // Trigger calendar files refresh
            window.dispatchEvent(new CustomEvent('calendarFilesUpdated'));
        
      } catch (uploadError) {
        console.error('Upload error:', uploadError);
        setParsingError(`Failed to upload file: ${uploadError.message}`);
            setUploadSuccess(''); // Clear success message
      } finally {
        // Remove file from processing set and mark as processed when done (success or error)
        setProcessingFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(fileObj.id);
          return newSet;
        });
        setProcessedFiles(prev => new Set(prev).add(fileObj.id));
      }
    }
  };

  // parseExcelFile function removed - backend now handles all Excel processing with dynamic cell structure

  // All Excel processing helper functions removed - backend now handles everything with dynamic cell structure

  // analyzeCellContent function removed - backend now handles all Excel processing with dynamic cell structure

  const uploadFiles = async () => {
    if (files.length === 0) return;
    
    setUploadStatus('uploading');
    setParsingError('');
    
    try {
      for (const fileObj of files) {
        if (fileObj.status === 'pending') {
          setUploadProgress(prev => ({ ...prev, [fileObj.id]: 0 }));
          
          // Simulate upload progress
          const interval = setInterval(() => {
            setUploadProgress(prev => {
              const newProgress = prev[fileObj.id] + Math.random() * 20;
              if (newProgress >= 100) {
                clearInterval(interval);
                setFiles(prev => prev.map(f => 
                  f.id === fileObj.id ? { ...f, status: 'completed' } : f
                ));
                
                // Process Excel file after upload completes (only once)
                // Add a small delay to ensure the file status is updated first
                // Check if file is already being processed to prevent race conditions
                if (!processingFiles.has(fileObj.id) && !processedFiles.has(fileObj.id)) {
                  setTimeout(() => {
                    processExcelFile(fileObj);
                  }, 100);
                } else {
                }
                
                return { ...prev, [fileObj.id]: 100 };
              }
              return { ...prev, [fileObj.id]: newProgress };
            });
          }, 200);
        }
      }
      
      setTimeout(() => {
        setUploadStatus('success');
        setTimeout(() => setUploadStatus('idle'), 3000);
      }, 3000);
      
    } catch (error) {
      console.error('Upload error:', error);
      setParsingError(`Upload failed: ${error.message}`);
      setUploadStatus('idle');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type.includes('excel') || type.includes('spreadsheet') || type.includes('xlsx') || type.includes('xls')) {
      return 'fa-solid fa-file-excel';
    }
    if (type.includes('csv')) return 'fa-solid fa-file-csv';
    if (type.includes('pdf')) return 'fa-solid fa-file-pdf';
    if (type.includes('word') || type.includes('document')) return 'fa-solid fa-file-word';
    if (type.includes('image')) return 'fa-solid fa-file-image';
    return 'fa-solid fa-file';
  };

  const getFileColor = (type) => {
    if (type.includes('excel') || type.includes('spreadsheet') || type.includes('xlsx') || type.includes('xls')) {
      return '#217346';
    }
    if (type.includes('csv')) return '#FF6B35';
    if (type.includes('pdf')) return '#DC3545';
    if (type.includes('word') || type.includes('document')) return '#0D6EFD';
    if (type.includes('image')) return '#6F42c1';
    return '#6C757D';
  };

  // Save Excel file to database - UPDATED FOR NEW DYNAMIC CELL STRUCTURE
  const saveToDatabase = async (file) => {
    try {
      // Get user/admin ID from localStorage or context
      const userToken = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      
      if (!userToken) {
        throw new Error('User not authenticated');
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      
      
      // Create FormData to send the file
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(baseUrl + '/api/excel-data/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`
          // Don't set Content-Type, let browser set it with boundary for FormData
        },
        body: formData
      });

      
      if (!response.ok) {
        const responseText = await response.text();
        console.error('❌ Response text:', responseText);
        throw new Error(`HTTP ${response.status}: ${responseText}`);
      }

      const result = await response.json();
      
      return result;
    } catch (error) {
      console.error('Error saving to database:', error);
      throw error;
    }
  };

  return (
    <div className="file-upload-container">
      <div className="container-fluid">
        <div className="row">
          {/* Header Section - col-md-6 */}
          <div className="col-md-6">
            <div className="upload-header">
              <div className="header-content">
                <div className="header-icon">
                  <i className="fa-solid fa-cloud-arrow-up"></i>
                </div>
                <div className="header-text">
                  <h1>Excel File Upload</h1>
                  <p>Upload your Excel sheets and spreadsheets easily</p>
                </div>
              </div>
              <div className="header-stats">
                <div className="stat-card">
                  <div className="stat-number">{files.length}</div>
                  <div className="stat-label">Files</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{files.filter(f => f.status === 'completed').length}</div>
                  <div className="stat-label">Done</div>
                </div>
              </div>
            </div>
          </div>

          {/* Upload Area - col-md-6 */}
          <div className="col-md-6">
            <div className="upload-area">
              <div
                className={`drag-drop-zone ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  multiple
                  accept=".xlsx,.xls,.csv"
                  onChange={handleChange}
                  className="file-input"
                  id="file-input"
                />
                <div className="upload-content">
                  <div className="upload-icon">
                    <i className="fa-solid fa-file-excel"></i>
                  </div>
                  <h3>Drop Excel files here</h3>
                  <p>or click to browse</p>
                  <div className="supported-formats">
                    <span className="format-badge">XLSX</span>
                    <span className="format-badge">XLS</span>
                    <span className="format-badge">CSV</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Files Section - Full Width */}
        {files.length > 0 && (
          <div className="row">
            <div className="col-12">
              <div className="files-section">
                <div className="section-header">
                  <h2>Selected Files ({files.length})</h2>
                  <button 
                    className="upload-all-btn"
                    onClick={uploadFiles}
                    disabled={uploadStatus === 'uploading' || files.every(f => f.status === 'completed')}
                  >
                    {uploadStatus === 'uploading' ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin"></i>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-upload"></i>
                        Upload All
                      </>
                    )}
                  </button>
                </div>
                
                <div className="files-grid">
                  {files.map(file => (
                    <div key={file.id} className={`file-card ${file.status}`}>
                      <div className="file-header">
                        <div className="file-icon" style={{ '--file-color': getFileColor(file.type) }}>
                          <i className={getFileIcon(file.type)}></i>
                        </div>
                        <div className="file-status">
                          {file.status === 'completed' && (
                            <i className="fa-solid fa-check-circle success-icon"></i>
                          )}
                          {file.status === 'pending' && (
                            <i className="fa-solid fa-clock pending-icon"></i>
                          )}
                        </div>
                      </div>
                      
                      <div className="file-info">
                        <h4 className="file-name" title={file.name}>{file.name}</h4>
                        <div className="file-meta">
                          <span className="file-size">{formatFileSize(file.size)}</span>
                          <span className="file-time">{file.uploadTime}</span>
                        </div>
                      </div>
                      
                      <div className="file-actions">
                        {file.status === 'pending' && (
                          <div className="upload-progress">
                            <div className="progress-bar">
                              <div 
                                className="progress-fill" 
                                style={{ width: `${uploadProgress[file.id] || 0}%` }}
                              ></div>
                            </div>
                            <span className="progress-text">{Math.round(uploadProgress[file.id] || 0)}%</span>
                          </div>
                        )}
                        
                        <button 
                          className="remove-btn"
                          onClick={() => removeFile(file.id)}
                          title="Remove file"
                        >
                          <i className="fa-solid fa-times"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upload Status - Full Width */}
        {uploadStatus === 'success' && (
          <div className="row">
            <div className="col-12">
              <div className="upload-success">
                <i className="fa-solid fa-check-circle"></i>
                <span>All files uploaded successfully!</span>
              </div>
            </div>
          </div>
        )}

        {/* Success Display */}
        {uploadSuccess && (
          <div className="row">
            <div className="col-12">
              <div className="upload-success">
                <i className="fa-solid fa-check-circle"></i>
                <span>{uploadSuccess}</span>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {parsingError && (
          <div className="row">
            <div className="col-12">
              <div className="upload-error">
                <i className="fa-solid fa-exclamation-triangle"></i>
                <span>{parsingError}</span>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default FileUpload;
